import { cn } from "@breakout/ui/utils"
import { Clock, Flame } from "lucide-react"
import { motion } from "motion/react"
import { memo, useId } from "react"
import type { TweetSortBy } from "../../lib/types"

interface TweetSortToggleProps {
	sortBy: TweetSortBy
	onSortChange: (sort: TweetSortBy) => void
}

const SORT_OPTIONS: Array<{ id: TweetSortBy; label: string; icon: typeof Flame }> = [
	{ id: "recent", label: "Recent", icon: Clock },
	{ id: "engagement", label: "Top", icon: Flame },
]

export const TweetSortToggle = memo(function TweetSortToggle({
	sortBy,
	onSortChange,
}: TweetSortToggleProps) {
	const layoutId = useId()

	return (
		<div
			className="inline-flex items-center gap-0.5 rounded-xl bg-bg-secondary p-1 ring-1 ring-border-subtle"
			role="tablist"
		>
			{SORT_OPTIONS.map(option => {
				const isActive = sortBy === option.id
				return (
					<button
						aria-selected={isActive}
						className={cn(
							"relative inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 font-medium text-sm transition-colors active:scale-[0.97]",
							isActive ? "text-text-primary" : "text-text-muted hover:text-text-secondary",
						)}
						key={option.id}
						onClick={() => onSortChange(option.id)}
						role="tab"
						type="button"
					>
						{isActive && (
							<motion.div
								className="absolute inset-0 rounded-lg bg-bg-card shadow-sm ring-1 ring-border-subtle"
								layoutId={layoutId}
								transition={{ type: "spring", stiffness: 500, damping: 35 }}
							/>
						)}
						<span className="relative z-10 inline-flex items-center gap-1.5">
							<option.icon aria-hidden="true" className="size-3.5" />
							{option.label}
						</span>
					</button>
				)
			})}
		</div>
	)
})
